import { useState } from 'react';
import { Alert, Button, SpaceBetween, Container } from '@amzn/awsui-components-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../app/hooks';
import DrishtiInstallGuideLink from '../../components/drishtiInstallGuideLink';
import DrishtiPageHeader from '../../components/drishtiPageHeader';
import DrishtiStationFacts from '../../components/drishtiStationFacts';
import { endDrishtiInstall, isEndDrishtiInstallResponse, type ErrorResponse } from '../../services/drishtiHandler';
import { EventName, EventType, logDrishtiMetricEvent } from '../../services/eventService';
import { updateDrishtiInstallEnded } from '../../slice/drishtiInstallConfigSlice';
import { formatStation, isCompleteStationConfig } from '../../constants/stationConfig';

const END_INSTALL_ERROR = 'Continue: could not record the install. Please try again.';

const endInstallErrorFor = (result: unknown) => {
    const message = typeof result === 'object' && result !== null ? (result as ErrorResponse).message : undefined;
    return message ? `${END_INSTALL_ERROR} (${message})` : END_INSTALL_ERROR;
};

const DrishtiGuide = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { site, stationNumber, deviceSerial, installStarted, installEnded, stationConfig } = useAppSelector(
        (state) => state.drishtiInstallConfig,
    );
    const [ending, setEnding] = useState(false);
    const [endError, setEndError] = useState('');

    if (!installStarted || !site || !stationNumber || !deviceSerial || !isCompleteStationConfig(stationConfig)) {
        return <Navigate to="/drishti/config" replace={true} />;
    }

    const station = { site, stationNumber };

    const logEndInstall = (eventType: EventType, miscEventData: {}) =>
        logDrishtiMetricEvent({
            eventName: EventName.DRISHTI_INSTALL_ENDED,
            eventType,
            deviceId: deviceSerial,
            miscEventData,
        });

    const onEndInstall = () => {
        setEnding(true);
        setEndError('');
        endDrishtiInstall({ deviceId: deviceSerial }, (success, result) => {
            setEnding(false);
            if (success && isEndDrishtiInstallResponse(result)) {
                dispatch(updateDrishtiInstallEnded(true));
                logEndInstall(EventType.SUCCESS, station);
                navigate('/drishti/calibration');
            } else {
                setEndError(endInstallErrorFor(result));
                logEndInstall(EventType.FAILURE, { ...station, error: result });
            }
        });
    };

    return (
        <Container>
            <DrishtiPageHeader
                title={`Installation at ${formatStation(station)}`}
                description={
                    installEnded
                        ? 'This install is recorded. Re-read any step you need, then continue to calibration.'
                        : 'Follow the installation guide for this workstation to move on to calibration.'
                }
            />
            <SpaceBetween size="l">
                <DrishtiStationFacts config={stationConfig} />

                <DrishtiInstallGuideLink />

                {endError ? <Alert type="error">{endError}</Alert> : null}

                {/* Recorded once, so a tech who comes back gets the way forward, not a second registration. */}
                {installEnded ? (
                    <Button variant="primary" onClick={() => navigate('/drishti/calibration')}>
                        Continue to calibration
                    </Button>
                ) : (
                    <Button variant="primary" onClick={onEndInstall} loading={ending}>
                        Continue
                    </Button>
                )}
            </SpaceBetween>
        </Container>
    );
};

export default DrishtiGuide;
